import { useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import SeoPageLayout from "@/components/seo/SeoPageLayout";
import Breadcrumbs from "@/components/seo/Breadcrumbs";
import FaqAccordion from "@/components/seo/FaqAccordion";
import CtaBlock from "@/components/seo/CtaBlock";
import RelatedGrid from "@/components/seo/RelatedGrid";
import useSeo from "@/hooks/useSeo";
import { getServiceBySlug, getRelatedServices } from "@/data/seo/services";
import { indianCities } from "@/data/seo/cities";
import {
  breadcrumbSchema,
  faqSchema,
  servicePageSchema,
  canonicalFor,
} from "@/lib/seoHelpers";

export default function ServicePage() {
  const { slug } = useParams();
  const svc = getServiceBySlug(slug);

  const canonical = canonicalFor(`/services/${slug}`);
  const crumbs = svc
    ? [
        { label: "Home", href: "/" },
        { label: "Services", href: "/services" },
        { label: svc.title, href: `/services/${slug}` },
      ]
    : [];

  useSeo({
    title: svc?.metaTitle,
    description: svc?.metaDescription,
    canonical,
    ogImage: svc?.heroImage,
  });

  if (!svc) return <Navigate to="/404" replace />;

  const schemas = [
    breadcrumbSchema(crumbs),
    faqSchema(svc.faqs),
    servicePageSchema({
      serviceName: svc.title,
      description: svc.metaDescription,
      slug: svc.slug,
      image: svc.heroImage,
    }),
  ];

  const related = getRelatedServices(slug).map((s) => ({
    href: `/services/${s.slug}`,
    label: s.title,
    eyebrow: s.eyebrow,
    description: s.metaDescription.slice(0, 120) + "…",
  }));

  // 3 India city links for internal linking
  const cityLinks = indianCities()
    .slice(0, 3)
    .map((c) => ({
      href: `/locations/${c.slug}`,
      label: `${svc.title} in ${c.city}`,
      eyebrow: "By Location",
      description: `Custom ${svc.title.toLowerCase()} for ${c.city} couples.`,
    }));

  return (
    <SeoPageLayout schemas={schemas}>
      <article
        data-testid={`service-page-${slug}`}
        className="max-w-4xl mx-auto px-6 md:px-10 lg:px-14 py-6 md:py-12"
      >
        <Breadcrumbs items={crumbs} />

        {/* Hero */}
        <div className="mb-14">
          <span className="overline text-[var(--iw-gold)]">
            {svc.eyebrow}
          </span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="font-serif-display text-[var(--iw-ivory)] text-4xl md:text-6xl lg:text-7xl leading-[1.02] mt-4 tracking-tight"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            {svc.title}
          </motion.h1>
          <p className="text-[var(--iw-muted)] text-lg md:text-xl leading-relaxed mt-8 max-w-3xl">
            {svc.intro}
          </p>
        </div>

        {/* Hero image */}
        <div
          className="relative aspect-[16/9] mb-14 overflow-hidden"
          style={{ border: "1px solid rgba(212,175,55,0.2)" }}
        >
          <img
            src={svc.heroImage}
            alt={`${svc.title} — InvitationWala portfolio example`}
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
          />
        </div>

        {/* Sections */}
        <div className="space-y-14 md:space-y-20">
          {svc.sections.map((sec, i) => (
            <section key={i} data-testid={`service-section-${i}`}>
              <div className="flex items-center gap-3 mb-6">
                <span className="w-8 h-px bg-[var(--iw-gold)]" />
                <h2
                  className="font-serif-display text-[var(--iw-ivory)] text-3xl md:text-4xl leading-tight"
                  style={{ fontFamily: "Cormorant Garamond, serif" }}
                >
                  {sec.h2}
                </h2>
              </div>
              {sec.paragraphs?.map((p, pi) => (
                <p
                  key={pi}
                  className="text-[var(--iw-muted)] leading-relaxed text-base md:text-lg mb-5"
                >
                  {p}
                </p>
              ))}
              {sec.bullets && (
                <ul className="mt-5 space-y-3">
                  {sec.bullets.map((b, bi) => (
                    <li
                      key={bi}
                      className="flex items-start gap-3 text-[var(--iw-muted)] leading-relaxed"
                    >
                      <span className="text-[var(--iw-gold)] mt-1 shrink-0">
                        ✦
                      </span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        {/* FAQ Section */}
        <section className="mt-20 md:mt-28" data-testid="service-faq-section">
          <div className="flex items-center gap-3 mb-8">
            <span className="w-8 h-px bg-[var(--iw-gold)]" />
            <h2
              className="font-serif-display text-[var(--iw-ivory)] text-3xl md:text-4xl leading-tight"
              style={{ fontFamily: "Cormorant Garamond, serif" }}
            >
              Frequently Asked Questions
            </h2>
          </div>
          <FaqAccordion faqs={svc.faqs} testid={`faq-${slug}`} />
        </section>

        <CtaBlock
          title={`Ready to commission your ${svc.title.toLowerCase()}?`}
          subtitle="Send us a WhatsApp brief — we respond within an hour with a transparent quote and reference reel."
          prefilledMessage={`Hi InvitationWala, I'd like to enquire about ${svc.title}.`}
          testid={`cta-${slug}`}
        />

        <RelatedGrid
          title="Related Services"
          items={related}
          testid="related-services"
        />

        <RelatedGrid
          title="Available in Cities"
          items={cityLinks}
          testid="service-city-links"
        />
      </article>
    </SeoPageLayout>
  );
}
